/**
 * Ce que la page d'un lien de présences et l'écran de gestion lisent et
 * écrivent (migration `20261024230000_les_liens_de_presences`).
 *
 * Côté animateur, tout passe par le jeton de l'adresse, sans compte : la base
 * vérifie le lien à chaque appel. Côté gestion, la création et la révocation
 * sont des fonctions réservées à l'administration et à la coordination ; la
 * liste des liens se lit dans la table, que la même règle protège.
 */
import { db } from '../payments/db';
import { lireEssentiels, lirePhotos, type LectureDesEssentiels } from './essentiels';
import {
  lireFiche,
  lirePresence,
  lireReponse,
  type JourCle,
  type LectureDeFiche,
  type LectureDuLien,
  type PresenceDeLEnfant,
} from './lien';

/* ------------------------------------------------------------------------ */
/*  Le téléphone de l'animateur                                              */
/* ------------------------------------------------------------------------ */

export async function lireLeLien(jeton: string, code: string | null): Promise<LectureDuLien> {
  const { data, error } = await db.rpc('presences_lien_lire', { p_jeton: jeton, p_code: code });
  if (error) throw error;
  return lireReponse(data);
}

/** La fiche d'un enfant : identité, parent, départ, santé, activités, inclusion, image. */
/**
 * Les essentiels des enfants du lien (santé brute, départ, espace des
 * familles, chemin de la photo) : lus une fois à l'ouverture.
 */
export async function lireLesEssentiels(jeton: string, code: string | null): Promise<LectureDesEssentiels> {
  const { data, error } = await db.rpc('presences_lien_essentiels', { p_jeton: jeton, p_code: code });
  if (error) throw error;
  return lireEssentiels(data);
}

export type LectureDesPhotos = { codeRequis: true } | { codeRequis: false; photos: Map<string, string> };

/**
 * Les adresses signées des photos, par la fonction Edge `photos-presences` :
 * l'espace `kid-photos` est privé, un téléphone sans compte ne le lit pas.
 * Valables une heure. Une fonction absente ou en panne lève : la page garde
 * les initiales et retentera au prochain ↻ — le 25/10/2026, la fonction
 * n'était pas déployée, et une carte vide rendue sans erreur aurait fait
 * attendre cinquante minutes avant le prochain essai.
 */
export async function lireLesPhotos(jeton: string, code: string | null): Promise<LectureDesPhotos> {
  const { data, error } = await db.functions.invoke('photos-presences', { body: { jeton, code } });
  if (error) {
    // `FunctionsHttpError` garde la réponse : un 403 « code requis » se lit dedans.
    const reponse = (error as { context?: Response }).context;
    if (reponse && typeof reponse.json === 'function') {
      const corps = (await reponse.json().catch(() => null)) as { code_requis?: boolean } | null;
      if (corps?.code_requis) return { codeRequis: true };
    }
    throw error;
  }
  if ((data as { code_requis?: boolean } | null)?.code_requis) return { codeRequis: true };
  return { codeRequis: false, photos: lirePhotos(data) };
}

export async function lireLaFiche(
  jeton: string,
  code: string | null,
  registrationId: string,
): Promise<LectureDeFiche> {
  const { data, error } = await db.rpc('presences_lien_enfant', {
    p_jeton: jeton,
    p_code: code,
    p_registration_id: registrationId,
  });
  if (error) throw error;
  return lireFiche(data);
}

export type ResultatDuGeste = { codeRequis: true } | { codeRequis: false; presence: PresenceDeLEnfant };

function lireGeste(data: unknown): ResultatDuGeste {
  const r = (data ?? {}) as { code_requis?: boolean } & Record<string, unknown>;
  if (r.code_requis) return { codeRequis: true };
  const presence = lirePresence(r as Parameters<typeof lirePresence>[0]);
  if (!presence) return { codeRequis: true };
  return { codeRequis: false, presence };
}

/** Présent (`true`), absent (`false`) ou à pointer (`null`) pour un jour. */
export async function pointer(
  jeton: string,
  code: string | null,
  registrationId: string,
  jour: JourCle,
  present: boolean | null,
): Promise<ResultatDuGeste> {
  const { data, error } = await db.rpc('presences_lien_pointer', {
    p_jeton: jeton,
    p_code: code,
    p_registration_id: registrationId,
    p_jour: jour,
    p_present: present,
    p_notes: null,
  });
  if (error) throw error;
  return lireGeste(data);
}

/** La note d'un enfant ; vide, elle s'efface. */
export async function noter(
  jeton: string,
  code: string | null,
  registrationId: string,
  notes: string,
): Promise<ResultatDuGeste> {
  const { data, error } = await db.rpc('presences_lien_pointer', {
    p_jeton: jeton,
    p_code: code,
    p_registration_id: registrationId,
    p_jour: null,
    p_present: null,
    p_notes: notes,
  });
  if (error) throw error;
  return lireGeste(data);
}

export type ResultatDuGroupe =
  | { codeRequis: true }
  | { codeRequis: false; presences: Array<PresenceDeLEnfant & { registrationId: string }> };

/** « Tous les autres sont là » : ceux encore à pointer, et eux seuls. */
export async function pointerLesAutres(
  jeton: string,
  code: string | null,
  registrationIds: readonly string[],
  jour: JourCle,
  present: boolean,
): Promise<ResultatDuGroupe> {
  const { data, error } = await db.rpc('presences_lien_pointer_les_autres', {
    p_jeton: jeton,
    p_code: code,
    p_registration_ids: [...registrationIds],
    p_jour: jour,
    p_present: present,
  });
  if (error) throw error;
  const r = (data ?? {}) as { code_requis?: boolean; presences?: Array<Record<string, unknown>> };
  if (r.code_requis) return { codeRequis: true };
  return {
    codeRequis: false,
    presences: (r.presences ?? []).flatMap((p) => {
      const presence = lirePresence(p as Parameters<typeof lirePresence>[0]);
      return presence ? [{ ...presence, registrationId: String(p.registration_id) }] : [];
    }),
  };
}

/* ------------------------------------------------------------------------ */
/*  L'écran de gestion                                                       */
/* ------------------------------------------------------------------------ */

export interface LienDeGestion {
  id: string;
  centerId: string;
  centre: string;
  semaineDebut: string;
  semaineFin: string;
  libelle: string;
  jeton: string;
  code: string | null;
  expireLe: string;
  creeLe: string;
  dernierUsageLe: string | null;
  pointages: number;
  essaisCode: number;
}

interface LienLu {
  id: string;
  center_id: string;
  semaine_debut: string;
  semaine_fin: string;
  libelle: string;
  jeton: string;
  code: string | null;
  expire_le: string;
  cree_le: string;
  dernier_usage_le: string | null;
  pointages: number;
  essais_code: number;
  centers: { name: string } | { name: string }[] | null;
}

const nomDuCentre = (c: LienLu['centers']): string =>
  (Array.isArray(c) ? c[0]?.name : c?.name) ?? '';

/** Les liens vivants d'une semaine (son lundi), par centre. */
export async function lireLesLiens(semaineDebut: string): Promise<LienDeGestion[]> {
  const { data, error } = await db
    .from('liens_presences')
    .select(
      'id, center_id, semaine_debut, semaine_fin, libelle, jeton, code, expire_le, cree_le, dernier_usage_le, pointages, essais_code, centers(name)',
    )
    .eq('semaine_debut', semaineDebut)
    .is('revoque_le', null)
    .order('cree_le');
  if (error) throw error;
  return ((data ?? []) as unknown as LienLu[])
    .map((l) => ({
      id: l.id,
      centerId: l.center_id,
      centre: nomDuCentre(l.centers),
      semaineDebut: l.semaine_debut,
      semaineFin: l.semaine_fin,
      libelle: l.libelle,
      jeton: l.jeton,
      code: l.code,
      expireLe: l.expire_le,
      creeLe: l.cree_le,
      dernierUsageLe: l.dernier_usage_le,
      pointages: l.pointages,
      essaisCode: l.essais_code,
    }))
    .sort((a, b) => a.centre.localeCompare(b.centre, 'fr'));
}

/** Un lien par centre qui a une session cette semaine-là ; les précédents révoqués. */
export async function creerLesLiens(semaineDebut: string, code: string | null): Promise<number> {
  const { data, error } = await db.rpc('presences_liens_creer', {
    p_semaine_debut: semaineDebut,
    p_code: code,
  });
  if (error) throw error;
  return Array.isArray(data) ? data.length : 0;
}

export async function revoquerLeLien(id: string): Promise<void> {
  const { error } = await db.rpc('presences_lien_revoquer', { p_lien_id: id });
  if (error) throw error;
}
